import { Icon } from "@iconify/react";
import Classnames from "classnames";
import React, { useEffect, useRef, useState } from "react";

import Button from "@shared/button";

import style from "./notification.styl";

type NotificationProps = {
  className?: string;
  text?: string;
  textClassName?: string;
  /**
   * Override default icon
   */
  icon?: React.ReactNode;
  /**
   * This will ignore text and show content instead
   */
  contents?: React.ReactNode;
  onClose?: () => void;
  open?: boolean;
  hideCloseButton?: boolean;
  /**
   * @default 'info'
   */
  type?: "info" | "success" | "warning" | "critical" | "message";
  centerIcon?: boolean;
  "data-testid"?: string;
};

const Notification = ({
  className,
  text,
  textClassName,
  icon,
  contents,
  onClose,
  open: openProp,
  hideCloseButton,
  type = "info",
  centerIcon,
  "data-testid": dataTestId,
}: NotificationProps) => {
  const [open, setOpen] = useState(openProp);
  const [close, setClose] = useState(!openProp);
  const timeoutRef = useRef<number>();

  useEffect(() => {
    window.clearTimeout(timeoutRef.current);

    if (openProp) {
      setClose(undefined);
      timeoutRef.current = window.setTimeout(() => setOpen(true), 100);
    } else {
      setOpen(undefined);
      timeoutRef.current = window.setTimeout(() => {
        setClose(true);
      }, 300);
    }

    return () => {
      setOpen(undefined);
      setClose(!openProp);
    };
  }, [openProp]);

  const classNameMerged = Classnames(
    style.notification,
    style[type],
    {
      [style.open]: open,
    },
    className,
  );

  // Enable closing
  if (close) return null;

  const getIcon = () => {
    if (icon) return icon;

    switch (type) {
      case "success":
        return <Icon icon="feather:check-circle" fontSize={22} />;
      case "critical":
        return <Icon icon="feather:alert-circle" fontSize={22} />;
      case "warning":
        return <Icon icon="feather:alert-triangle" fontSize={22} />;
      default:
        return <Icon icon="feather:info" fontSize={22} />;
    }
  };

  return (
    <div className={classNameMerged} data-testid={dataTestId}>
      <div className={style.content}>
        <span
          className={Classnames(style.icon, { [style.centered]: centerIcon })}
        >
          {getIcon()}
        </span>
        {contents ? (
          contents
        ) : (
          <div className={Classnames(style.text, textClassName)}>{text}</div>
        )}
      </div>
      {!hideCloseButton && (
        <Button
          size="tiny"
          iconColors
          iconButton
          transparent
          onClick={() => {
            setOpen(undefined);
            setTimeout(() => setClose(true), 300);
            onClose?.();
          }}
        >
          <Icon icon="feather:x" />
        </Button>
      )}
    </div>
  );
};

Notification.displayName = "Notification";

export default Notification;
